import React, { useEffect } from 'react';
import { TypedMemo } from '../../common/types';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { useUser } from '../../users/hooks/useUser';
import { removeSignedInHint, storeSignedInHint, storeSignedInHintIfMissing } from './signedInHintStore';

/**
 * Keeps the signed-in hint for music24.com in step with the session (design website-v2 k):
 * - signed in: a note exists at once (with no name), and it gets the first name when
 *   `GET /users/:id` answers. Each answer writes a new time, so an old note expires on the website.
 * - signed out (no token, or the token went away): the note goes.
 *
 * The store reads the token before the first render (`ReduxLoader`), so a signed-in reload never
 * removes the note for a moment.
 */
const SignedInHintSyncComponent: React.FC = () => {
  const userToken = useUserToken();
  const signedIn = Boolean(userToken);
  const { data } = useUser();
  const hasAnswer = data !== undefined;
  // An older server sends no field: then the note has no name.
  const firstName = data?.user.firstName ?? null;

  useEffect(() => {
    if (!signedIn) {
      removeSignedInHint();
      return;
    }
    if (hasAnswer) {
      storeSignedInHint(firstName);
    } else {
      // Keep the name of an earlier note until the answer is in.
      storeSignedInHintIfMissing();
    }
  }, [firstName, hasAnswer, signedIn]);

  return null;
};

export const SignedInHintSync = TypedMemo(SignedInHintSyncComponent);
